import React, { useEffect, useState } from 'react'
import { Button, Checkbox, message, Spin, Tabs, Tag, Tooltip, theme } from 'antd'
import { SaveOutlined, ReloadOutlined } from '@ant-design/icons'
import axiosInstance from '@/lib/axios'

interface Role {
  id: number
  codice: string
  nome: string
}

interface Permission {
  id: number
  codice: string
  nome: string
  categoria: string
  descrizione: string
  roles: {
    ruolo_id: number
    ruolo_codice: string
    granted: boolean
  }[]
}

// Mapping statico: tab → codici permesso rilevanti.
// I permessi possono ripetersi in più tab intenzionalmente (chiarezza per contesto).
const TAB_GROUPS: { key: string; label: string; codici: string[] }[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    codici: [
      'dashboard.view',
      'dashboard.full_view',
      'dashboard.stats',
      'dashboard.stati.bozza',
      'dashboard.stati.accettazione',
      'dashboard.stati.approvata',
      'dashboard.stati.in_analisi',
      'dashboard.stati.in_approvazione',
      'dashboard.stati.in_lavorazione',
      'dashboard.stati.sospesa',
      'dashboard.stati.completata',
      'dashboard.stati.riconsegnata',
      'dashboard.stati.annullata',
      'analisi.view',
    ],
  },
  {
    key: 'accettazione',
    label: 'Accettazione',
    codici: [
      'pre_accettazione.create',
      'work_orders.view',
      'work_orders.create',
      'work_orders.edit',
      'work_orders.cancel',
      'work_orders.delete',
      'interventions.view',
      'interventions.create',
      'interventions.edit',
      'interventions.delete',
      'interventions.update_status',
      'documents.view',
      'documents.edit',
      'documents.delete',
    ],
  },
  {
    key: 'schede',
    label: 'Schede',
    codici: [
      'work_orders.view_own',
      'work_orders.operativo',
      'work_orders.delete_any',
      'dashboard.stati.bozza',
      'dashboard.stati.accettazione',
      'dashboard.stati.approvata',
      'dashboard.stati.in_analisi',
      'dashboard.stati.in_approvazione',
      'dashboard.stati.in_lavorazione',
      'dashboard.stati.sospesa',
      'dashboard.stati.completata',
      'dashboard.stati.riconsegnata',
      'dashboard.stati.annullata',
      'interventions.view',
      'interventions.create',
      'interventions.edit',
      'interventions.delete',
      'interventions.update_status',
    ],
  },
  {
    key: 'clienti',
    label: 'Clienti',
    codici: [
      'customers.view',
      'customers.create',
      'customers.edit',
      'customers.delete',
    ],
  },
  {
    key: 'veicoli',
    label: 'Veicoli',
    codici: [
      'vehicles.view',
      'vehicles.create',
      'vehicles.edit',
      'vehicles.delete',
    ],
  },
  {
    key: 'fornitori',
    label: 'Fornitori',
    codici: [
      'suppliers.view',
      'suppliers.create',
      'suppliers.edit',
      'suppliers.delete',
    ],
  },
  {
    key: 'rda',
    label: 'RDO',
    codici: [
      'purchase_requests.view',
      'purchase_requests.create',
      'purchase_requests.edit',
      'purchase_requests.delete',
      'purchase_requests.approve',
      'purchase_requests.order',
      'purchase_requests.receive',
      'rdos.view',
      'rdos.create',
      'rdos.edit',
      'rdos.delete',
      'rdos.select',
      'rdo_templates.view',
      'rdo_templates.manage',
    ],
  },
  {
    key: 'costi',
    label: 'Costi',
    codici: [
      'costs.view',
      'costs.view_margin',
      'costs.create',
      'costs.edit',
      'costs.delete',
      'costs.manage_rates',
    ],
  },
  {
    key: 'pagamenti',
    label: 'Pagamenti',
    codici: [
      'payments.view',
      'payments.create',
      'payments.edit',
      'payments.delete',
    ],
  },
  {
    key: 'cortesia',
    label: 'Cortesia',
    codici: [
      'courtesy_cars.view',
      'courtesy_cars.create',
      'courtesy_cars.edit',
      'courtesy_cars.delete',
      'courtesy_cars.loan',
      'courtesy_cars.return',
      'courtesy_cars.manage',
    ],
  },
  {
    key: 'manutenzioni',
    label: 'Manutenzioni',
    codici: [
      'maintenance.view',
      'maintenance.create',
      'maintenance.delete',
      'tires.view',
      'tires.rotation',
      'tires.create',
      'tires.edit',
      'tires.delete',
    ],
  },
  {
    key: 'ricambi',
    label: 'Ricambi',
    codici: [
      'parts.view',
      'parts.create',
      'parts.edit',
      'parts.delete',
      'parts.manage_stock',
    ],
  },
  {
    key: 'servizio_gomme',
    label: 'Servizio Gomme',
    codici: [
      'tire_service.view',
      'tire_service.checkin',
      'tire_service.manage',
      'tire_service.reports',
    ],
  },
  {
    key: 'comunicazioni',
    label: 'Comunicazioni',
    codici: [
      'comunicazioni.view',
      'comunicazioni.send',
      'comunicazioni.programmati',
    ],
  },
  {
    key: 'calendario',
    label: 'Calendario',
    codici: [
      'calendar.view',
      'calendar.create',
      'calendar.edit',
      'calendar.delete',
      'calendar.manage',
    ],
  },
  {
    key: 'board',
    label: 'Board',
    codici: [
      'board_giornaliero.view',
      'board_giornaliero.edit',
      'courtesy_cars.view',
    ],
  },
  {
    key: 'officine',
    label: 'Officine',
    codici: [
      'system.manage_users',
      'system.manage_workshops',
    ],
  },
  {
    key: 'officine_esterne',
    label: 'Officine esterne',
    codici: [
      'workshop_quotes.view',
      'workshop_quotes.request',
      'workshop_quotes.respond',
      'workshop_quotes.approve',
    ],
  },
  {
    key: 'impostazioni',
    label: 'Impostazioni',
    codici: [
      'system.settings',
      'system.manage_permissions',
      'system.manage_roles',
      'system.manage_users',
      'system.manage_workshops',
      'system.audit_logs',
      'activity_logs.view',
      'notifications.view',
      'notifications.manage',
    ],
  },
]

export const RolePermissionsMatrix: React.FC<{ refreshKey?: number }> = ({ refreshKey = 0 }) => {
  const { token } = theme.useToken()
  const [permissions, setPermissions] = useState<Permission[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [roles, setRoles] = useState<Role[]>([])
  const [changes, setChanges] = useState<{ [key: string]: boolean }>({})

  useEffect(() => {
    fetchMatrix()
  }, [refreshKey])

  const fetchMatrix = async () => {
    try {
      setLoading(true)
      const response = await axiosInstance.get('/permissions/matrix')
      setPermissions(response.data.permissions)
      setRoles(response.data.roles)
      setChanges({})
    } catch (error) {
      message.error('Errore nel caricamento della matrice permessi')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const getPermissionValue = (perm: Permission, role: Role): boolean => {
    const key = `${role.codice}-${perm.id}`
    if (Object.prototype.hasOwnProperty.call(changes, key)) {
      return changes[key]
    }
    const roleData = perm.roles.find(r => r.ruolo_codice === role.codice)
    return roleData?.granted ?? false
  }

  const handleToggle = (permissionId: number, role: Role, granted: boolean) => {
    const key = `${role.codice}-${permissionId}`
    const newValue = !granted
    const originalGranted = permissions
      .find(p => p.id === permissionId)
      ?.roles.find(r => r.ruolo_codice === role.codice)
      ?.granted ?? false
    setChanges(prev => {
      if (newValue === originalGranted) {
        const next = { ...prev }
        delete next[key]
        return next
      }
      return { ...prev, [key]: newValue }
    })
  }

  const handleSelectAllForRole = (role: Role, selectAll: boolean) => {
    const newChanges = { ...changes }
    for (const perm of permissions) {
      const key = `${role.codice}-${perm.id}`
      const currentValue = getPermissionValue(perm, role)
      if (selectAll) {
        if (!currentValue) newChanges[key] = true
        else delete newChanges[key]
      } else {
        if (currentValue) newChanges[key] = false
        else delete newChanges[key]
      }
    }
    setChanges(newChanges)
  }

  const isRoleSelectAll = (role: Role): boolean =>
    permissions.every(perm => getPermissionValue(perm, role))

  const handleColumnSelectAll = (role: Role, tabPerms: Permission[], selectAll: boolean) => {
    const newChanges = { ...changes }
    for (const perm of tabPerms) {
      const key = `${role.codice}-${perm.id}`
      const currentValue = getPermissionValue(perm, role)
      if (selectAll) {
        if (!currentValue) newChanges[key] = true
        else delete newChanges[key]
      } else {
        if (currentValue) newChanges[key] = false
        else delete newChanges[key]
      }
    }
    setChanges(newChanges)
  }

  const isColumnSelectAll = (role: Role, tabPerms: Permission[]): boolean =>
    tabPerms.length > 0 && tabPerms.every(perm => getPermissionValue(perm, role))

  const saveMatrix = async () => {
    try {
      setSaving(true)
      const payload: { [key: string]: { [key: string]: boolean } } = {}
      for (const role of roles) {
        payload[role.id.toString()] = {}
        for (const perm of permissions) {
          payload[role.id.toString()][perm.id.toString()] = getPermissionValue(perm, role)
        }
      }
      await axiosInstance.put('/permissions/matrix', payload)
      message.success('Permessi aggiornati con successo')
      setChanges({})
      await fetchMatrix()
    } catch (error) {
      message.error('Errore nel salvataggio dei permessi')
      console.error(error)
    } finally {
      setSaving(false)
    }
  }

  const hasChanges = Object.keys(changes).length > 0

  // Render della tabella matrice per un sottoinsieme di permessi
  const renderMatrix = (tabPerms: Permission[]) => {
    if (tabPerms.length === 0) {
      return (
        <div style={{ padding: '24px', color: token.colorTextTertiary, textAlign: 'center', fontSize: 13 }}>
          Nessun permesso configurato per questa sezione.
        </div>
      )
    }

    return (
      <div style={{ overflowX: 'auto', border: '1px solid #d9d9d9', borderRadius: 4 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ backgroundColor: token.colorFillAlter, borderBottom: `1px solid ${token.colorBorder}` }}>
              <th style={{
                padding: '8px 10px',
                textAlign: 'left',
                fontWeight: 600,
                borderRight: '1px solid #d9d9d9',
                minWidth: 220,
              }}>
                Permesso
              </th>
              {roles.map(role => (
                <th
                  key={role.id}
                  style={{
                    padding: '6px 4px',
                    textAlign: 'center',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    minWidth: 80,
                    borderRight: '1px solid #d9d9d9',
                    fontSize: 11,
                  }}
                >
                  <Tooltip title={role.nome}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                      <Tag color="blue" style={{ margin: 0, fontSize: 10, cursor: 'default' }}>
                        {role.codice.length > 7 ? role.codice.substring(0, 7) + '…' : role.codice}
                      </Tag>
                      <Tooltip title={`Seleziona/deseleziona tutti i permessi di questa sezione per ${role.nome}`}>
                        <Checkbox
                          checked={isColumnSelectAll(role, tabPerms)}
                          onChange={e => handleColumnSelectAll(role, tabPerms, e.target.checked)}
                        />
                      </Tooltip>
                    </div>
                  </Tooltip>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tabPerms.map(perm => (
              <tr key={perm.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{
                  padding: '6px 10px',
                  borderRight: '1px solid #d9d9d9',
                  fontSize: 12,
                }}>
                  <div>
                    <div style={{ fontWeight: 500 }}>{perm.nome}</div>
                    <div style={{ color: token.colorTextTertiary, fontSize: 10, fontFamily: 'monospace' }}>{perm.codice}</div>
                    {perm.descrizione && (
                      <div style={{ color: token.colorTextSecondary, fontSize: 11, marginTop: 2, lineHeight: '1.4' }}>
                        {perm.descrizione}
                      </div>
                    )}
                  </div>
                </td>
                {roles.map(role => {
                  const isGranted = getPermissionValue(perm, role)
                  const isChanged = Object.prototype.hasOwnProperty.call(changes, `${role.codice}-${perm.id}`)
                  return (
                    <td
                      key={role.id}
                      style={{
                        padding: 4,
                        textAlign: 'center',
                        backgroundColor: isChanged ? '#e6f7ff' : 'transparent',
                        borderRight: '1px solid #d9d9d9',
                      }}
                    >
                      <Checkbox
                        checked={isGranted}
                        onChange={() => handleToggle(perm.id, role, isGranted)}
                      />
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  // Costruisce le tab items: una per gruppo + tab "Tutti"
  const allMappedCodici = new Set(TAB_GROUPS.flatMap(g => g.codici))
  const uncategorized = permissions.filter(p => !allMappedCodici.has(p.codice))

  const tabItems = [
    ...TAB_GROUPS.map(group => {
      const tabPerms = permissions.filter(p => group.codici.includes(p.codice))
      const changedCount = tabPerms.filter(p =>
        roles.some(r => Object.prototype.hasOwnProperty.call(changes, `${r.codice}-${p.id}`))
      ).length
      return {
        key: group.key,
        label: (
          <span>
            {group.label}
            {changedCount > 0 && (
              <Tag color="blue" style={{ marginLeft: 6, fontSize: 10 }}>{changedCount}</Tag>
            )}
          </span>
        ),
        children: <div style={{ paddingTop: 8 }}>{renderMatrix(tabPerms)}</div>,
      }
    }),
    ...(uncategorized.length > 0 ? [{
      key: 'altro',
      label: <span>Altro <Tag color="orange" style={{ fontSize: 10 }}>{uncategorized.length}</Tag></span>,
      children: <div style={{ paddingTop: 8 }}>{renderMatrix(uncategorized)}</div>,
    }] : []),
    {
      key: 'tutti',
      label: 'Tutti',
      children: <div style={{ paddingTop: 8 }}>{renderMatrix(permissions)}</div>,
    },
  ]

  return (
    <div>
      {/* Barra di controllo — fuori dai tab, sempre visibile */}
      <div style={{ marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button
          icon={<ReloadOutlined />}
          onClick={fetchMatrix}
          loading={loading}
          size="small"
        >
          Ricarica
        </Button>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={saveMatrix}
          loading={saving}
          disabled={!hasChanges}
          size="small"
        >
          Salva {hasChanges ? `(${Object.keys(changes).length})` : ''}
        </Button>
        {hasChanges && (
          <span style={{
            padding: '2px 10px',
            backgroundColor: '#e6f7ff',
            border: '1px solid #91d5ff',
            borderRadius: 4,
            color: '#0050b3',
            fontSize: 12,
          }}>
            ⚠️ {Object.keys(changes).length} modifica/he non salvata/e
          </span>
        )}
      </div>

      {/* Seleziona tutti per ruolo — controllo globale */}
      <div style={{ marginBottom: 12, padding: '8px 12px', backgroundColor: token.colorFillAlter, borderRadius: 4 }}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: token.colorTextSecondary }}>
          Seleziona tutti i permessi per ruolo:
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {roles.map(role => (
            <Tooltip key={role.id} title={`Seleziona/deseleziona tutti i permessi per ${role.nome}`}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Checkbox
                  checked={isRoleSelectAll(role)}
                  onChange={e => handleSelectAllForRole(role, e.target.checked)}
                />
                <span style={{ fontSize: 12 }}>{role.nome}</span>
              </div>
            </Tooltip>
          ))}
        </div>
      </div>

      <Spin spinning={loading}>
        <Tabs
          type="card"
          size="small"
          items={tabItems}
          tabBarStyle={{ marginBottom: 0 }}
        />
      </Spin>
    </div>
  )
}

export default RolePermissionsMatrix
